/**
 * v2 Locks — active lock matrix. Read-only.
 *
 * One row per active hard lock (``chassis_locks`` rows with
 * ``released_at IS NULL AND hard = TRUE``). Shows who's holding what
 * + how long; the lock auto-releases when the owning job completes
 * or expires.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { HelpButton } from "../../help/HelpPanel";

type LockRow = {
  id: number;
  scope_type: string;
  scope_key: string;
  job_id: string | null;
  holder_gigafy_username: string;
  hard: boolean;
  acquired_at: string;
  expires_at: string;
};

type LockMatrixResponse = { locks: LockRow[] };

export function Locks() {
  const q = useQuery<LockMatrixResponse>({
    queryKey: ["v2", "locks", "matrix"],
    queryFn: async () => {
      const res = await api.get<LockMatrixResponse>("/locks/matrix");
      return res.data;
    },
    refetchInterval: 5_000,
  });

  const locks = q.data?.locks ?? [];

  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Active locks</h1>
          <HelpButton topic="tools-locks" />
        </div>
        <p className="text-xs text-slate-500">
          Hard locks held by destructive operations. Auto-release on
          job completion or expiry.
        </p>
      </header>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : locks.length === 0 ? (
          <p className="text-xs text-slate-500">No active locks.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Scope</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">Holder</th>
                <th className="px-2 py-1">Job</th>
                <th className="px-2 py-1">Acquired</th>
                <th className="px-2 py-1">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {locks.map((l) => (
                <tr key={l.id}>
                  <td className="px-2 py-1.5">
                    <CopyableCell value={l.scope_key} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={l.hard ? "danger" : "info"}>
                      {l.hard ? "hard" : "soft"} · {l.scope_type}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5">{l.holder_gigafy_username}</td>
                  <td className="px-2 py-1.5">
                    {l.job_id ? (
                      <a
                        href={`/settings/jobs/${l.job_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {l.job_id.slice(0, 8)}…
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={l.acquired_at} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={l.expires_at} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
